import { CONFIG } from '@hero/core';
import { VehicleTypes } from '@hero/schema';
import { formatVehicleText } from '@hero/web-utils';
import { IconProps, IconSize, IconVariant, VehicleDynamicIcon } from '@hero/web-icons';
import { SelectAuto, SelectAutoGroup, SelectAutoOption } from '@hero/web-components';
import { useTranslation } from 'react-i18next';
import { cn } from '@hero/web-lib';
import { useVehicleOptions } from '@hero/hooks-shared';

interface VehicleTypeSelectorProps {
  /** Current selected vehicle type value */
  value?: VehicleTypes | string;
  /** Callback when vehicle type changes */
  onVehicleChange: (vehicleType: VehicleTypes) => void;
  /** Optional placeholder text */
  placeholder?: string;
  /** Optional className for the select trigger */
  className?: string;
  /** Optional icon props */
  iconProps?: IconProps;
  /** Optional disabled state */
  disabled?: boolean;
}

/**
 * VehicleTypeSelector - A complete input component for selecting vehicle types.
 * Includes the select field with vehicle icons and labels.
 *
 * @example
 * ```tsx
 * <VehicleTypeSelector
 *   value={selectedVehicle}
 *   onVehicleChange={(vehicleType) => setSelectedVehicle(vehicleType)}
 *   placeholder="Select vehicle type"
 * />
 * ```
 */
export function VehicleTypeSelector({
  value,
  onVehicleChange,
  placeholder,
  className,
  iconProps,
  disabled = false,
}: VehicleTypeSelectorProps) {
  const { t } = useTranslation('m-vehicle');
  const { allVehicles, groupedVehicles, getVehicleSpec } = useVehicleOptions();

  const options: SelectAutoGroup[] = Object.entries(groupedVehicles).map(
    ([groupKey, vehicles]) => ({
      label: t(`groups.${groupKey}`, groupKey),
      options: vehicles.map(vehicle => ({
        value: vehicle.id,
        label: t(`types.${vehicle.id}`),
        icon: (
          <VehicleDynamicIcon
            vehicle={vehicle.id}
            size={'medium'}
            variant={'primary'}
            {...iconProps}
          />
        ),
      })),
    }),
  );

  return (
    <SelectAuto
      options={options}
      value={value}
      onChange={val => onVehicleChange(val as VehicleTypes)}
      placeholder={placeholder || t('vehicle')}
      disabled={disabled}
      className={className}
      triggerClassName={cn('text-[16px] font-[600] leading-[24px]', className)}
    />
  );
}
